import { MINDTIME_PATHS } from 'src/constants/mindtime/constants';

export const MAIN_SIDEBAR_PATHS = {
  DASHBOARD: '/cases/dashboard',
  SSL_OVERVIEW: '/dashboard/ssl-overview',
  BACKUP_OVERVIEW_DASH: '/dashboard/backup-overview',
  DYNAMIC_DASHBOARD: '/dashboard/dynamic',
  CLIENTS: '/clients',
  MY_CLIENTS: '/mindtime/users',
  BACKUP_OVERVIEW: '/backup/overview',
  MY_BACKUPS: '/mindtime/my-backups',
  BACKUP_PORTAL: MINDTIME_PATHS.BACKUP_PORTAL,
  SECURITY_OVERVIEW: MINDTIME_PATHS.SECURITY_SOC,
  SECURITY_SSL: '/security/ssl',
  CASES: '/cases/all',
  HISTORY: '/mindtime/history',
  SUPPORT_KB: '/mindtime/support',
  SUPPORT_SERVICE_STATUS: '/mindtime/support/service-status',
  SUPPORT_DOWNLOADS: '/mindtime/support/downloads',
  PROJECTS: '/projects',
  PLANNING: '/planning',
  REPORTS: '/reporting',
  SUBSCRIPTIONS: '/subscriptions',
  ORDERS: '/orders',
  PRICES: '/mindtime/prices',
  USERS: '/contacts',
  LISTS: '/mindtime/lists',
  ROLES: '/administration/roles-permissions',
  AUDIT_LOG: '/administration/audit-log',
  /** Hidden from sidebar for now; route may still exist. */
  HOME: '/mindtime/home',
} as const;

export type MainNavGroupId =
  | 'dashboard'
  | 'clients'
  | 'backup'
  | 'security'
  | 'support'
  | 'projects'
  | 'reports'
  | 'billing'
  | 'administration';

/** Paths that appear as children in more than one main sidebar group. */
export const SHARED_MAIN_NAV_PATHS: ReadonlyArray<{
  path: string;
  groups: readonly MainNavGroupId[];
}> = [
  { path: MAIN_SIDEBAR_PATHS.DASHBOARD, groups: ['dashboard', 'projects'] },
  { path: MAIN_SIDEBAR_PATHS.SECURITY_OVERVIEW, groups: ['dashboard', 'security'] },
];

export const pathMatches = (pathname: string, target: string) =>
  pathname === target || pathname.startsWith(`${target}/`);

export const getSharedMainNavEntry = (pathname: string) =>
  SHARED_MAIN_NAV_PATHS.find(({ path }) => pathMatches(pathname, path));

export const isSharedMainNavGroup = (groupId: MainNavGroupId) =>
  SHARED_MAIN_NAV_PATHS.some(({ groups }) => groups.includes(groupId));


/** Cases covers all `/cases/*` except the shared dashboard route. */
export const isMainNavChildActive = (pathname: string, to: string) => {
  if (to === MAIN_SIDEBAR_PATHS.CASES) {
    return pathname.includes('/cases') && !pathname.includes('/cases/dashboard');
  }
  // Knowledge Base owns `/mindtime/support` + article paths, not sibling Support leaves.
  if (to === MAIN_SIDEBAR_PATHS.SUPPORT_KB) {
    return (
      pathname === MAIN_SIDEBAR_PATHS.SUPPORT_KB ||
      pathMatches(pathname, `${MAIN_SIDEBAR_PATHS.SUPPORT_KB}/knowledge-base`)
    );
  }
  // Create flow lives at `/create-subscription`, not under `/subscriptions/...`.
  if (to === MAIN_SIDEBAR_PATHS.SUBSCRIPTIONS) {
    return (
      pathMatches(pathname, MAIN_SIDEBAR_PATHS.SUBSCRIPTIONS) ||
      pathname.includes('/create-subscription')
    );
  }
  return pathMatches(pathname, to);
};

/**
 * Map pathname → open SubMenu group.
 * Prefer Dashboard over Projects when both share `/cases/dashboard`.
 * Prefer Security over Dashboard when both share SOC (no intent).
 */
export const getOpenGroupFromPathname = (pathname: string): MainNavGroupId | null => {
  if (pathname.includes('/cases/dashboard') || pathname.startsWith('/dashboard/')) {
    return 'dashboard';
  }
  if (pathMatches(pathname, MAIN_SIDEBAR_PATHS.CLIENTS) || pathname.includes('/mindtime/users')) {
    return 'clients';
  }
  if (
    pathname.startsWith('/backup/') ||
    pathname.includes('/mindtime/my-backups') ||
    pathname.includes('/mindtime/backup-portal')
  ) {
    return 'backup';
  }
  if (
    pathname.includes('/mindtime/security') ||
    pathname.includes('/mindtime/wazu') ||
    pathname.startsWith('/security/')
  ) {
    return 'security';
  }
  if (
    pathname.includes('/mindtime/support') ||
    pathname.includes('/mindtime/history') ||
    (pathname.includes('/cases') && !pathname.includes('/cases/dashboard'))
  ) {
    return 'support';
  }
  if (pathname.includes('/projects') || pathname.includes('/planning')) {
    return 'projects';
  }
  if (pathname.includes('/reporting')) {
    return 'reports';
  }
  if (
    pathname.includes('/subscriptions') ||
    pathname.includes('/create-subscription') ||
    pathname.includes('/orders') ||
    pathname.includes('/create-order') ||
    pathname.includes('/mindtime/prices')
  ) {
    return 'billing';
  }
  if (
    pathname.includes('/contacts') ||
    pathname.includes('/mindtime/lists') ||
    pathname.startsWith('/administration/')
  ) {
    return 'administration';
  }
  return null;
};
